import { useCallback, useEffect, useId, useState } from 'react';

import { Badge, Button, Card, Icon } from '@ds/core';
import { DataTable } from '@ds/data';
import type { DataTableColumn } from '@ds/data';
import { Alert, Modal } from '@ds/feedback';
import { FormField, Input } from '@ds/forms';
import { Menu } from '@ds/navigation';
import { useAlteracaoPendente } from '@/comuns/useAlteracaoPendente';
import { useAviso } from '@/comuns/toast';
import { dados } from '@/data/porta';
import type { TipoDeServico } from '@/data/tipos';

/**
 * Os tipos de serviço dos prestadores (0159), em Financeiro › Categorias de
 * contas (decisão do usuário, 17/09/2026). Tipo em uso não se apaga: inativa,
 * sai da lista de escolha, e o prestador continua com ele.
 */
export function TiposDeServico() {
    const aviso = useAviso();
    const [tipos, setTipos] = useState<TipoDeServico[] | null>(null);
    const [erro, setErro] = useState<string | null>(null);
    const [editando, setEditando] = useState<TipoDeServico | null | undefined>(undefined);

    const carregar = useCallback(async () => {
        try {
            setTipos(await dados().tiposDeServico());
            setErro(null);
        } catch (e) {
            setErro(e instanceof Error ? e.message : 'Não foi possível ler os tipos de serviço.');
        }
    }, []);

    useEffect(() => { void carregar(); }, [carregar]);

    const alternar = async (tipo: TipoDeServico) => {
        try {
            await dados().salvarTipoDeServico({ id: tipo.id, nome: tipo.nome, ativo: !tipo.ativo });
            aviso({
                title: tipo.ativo ? 'Tipo de serviço inativado' : 'Tipo de serviço reativado',
                description: tipo.ativo ? `${tipo.nome} sai da lista de escolha; os prestadores continuam com ele` : tipo.nome,
                tone: tipo.ativo ? 'warning' : 'success',
            });
            await carregar();
        } catch (e) {
            aviso({ title: 'Não foi possível mudar o tipo de serviço', description: e instanceof Error ? e.message : undefined, tone: 'danger' });
        }
    };

    const colunas: DataTableColumn<TipoDeServico>[] = [
        { key: 'nome', header: 'Tipo de serviço', sortable: true, render: (t) => <strong style={{ fontWeight: 600 }}>{t.nome}</strong> },
        {
            key: 'prestadores', header: 'Prestadores', align: 'right', sortable: true,
            render: (t) => (t.prestadores === 0 ? <span style={{ color: 'var(--text-tertiary)' }}>nenhum</span> : t.prestadores),
        },
        { key: 'ativo', header: 'Situação', render: (t) => <Badge tone={t.ativo ? 'success' : 'neutral'} dot>{t.ativo ? 'Ativo' : 'Inativo'}</Badge> },
        {
            key: 'acoes', header: '', align: 'right', width: 56,
            render: (t) => (
                <span data-parar-linha>
                    <Menu
                        align="right" width={220}
                        onSelect={(id) => {
                            if (id === 'editar') setEditando(t);
                            else if (id === 'situacao') void alternar(t);
                        }}
                        trigger={<Button size="sm" variant="ghost" iconLeft={<Icon name="ellipsis" size={15} />} aria-label={`Ações de ${t.nome}`} />}
                        items={[
                            { id: 'editar', label: 'Renomear', icon: 'pencil' },
                            { id: 'divisor', label: '', divider: true },
                            {
                                id: 'situacao', label: t.ativo ? 'Inativar tipo' : 'Reativar tipo',
                                icon: t.ativo ? 'ban' : 'rotate-ccw', tone: t.ativo ? 'danger' : undefined,
                            },
                        ]}
                    />
                </span>
            ),
        },
    ];

    return (
        <Card
            title="Tipos de serviço" subtitle="Usados no cadastro de prestadores de serviço, em Contatos" padding={18}
            actions={<Button size="sm" iconLeft={<Icon name="plus" size={14} />} onClick={() => setEditando(null)}>Novo tipo</Button>}
        >
            {erro && <Alert tone="danger" dismissible={false}>{erro}</Alert>}
            {tipos && (
                <DataTable
                    columns={colunas} rows={tipos} rowKey="id"
                    onRowClick={setEditando}
                    rowLabel={(t) => t.nome}
                    emptyMessage="Nenhum tipo de serviço cadastrado."
                />
            )}
            <TipoDeServicoModal
                open={editando !== undefined} tipo={editando ?? null}
                onClose={() => setEditando(undefined)}
                onSalvo={() => void carregar()}
            />
        </Card>
    );
}

function TipoDeServicoModal({ open, tipo, onClose, onSalvo }: {
    open: boolean;
    tipo: TipoDeServico | null;
    onClose: () => void;
    onSalvo: () => void;
}) {
    const aviso = useAviso();
    const idNome = useId();
    const [nome, setNome] = useState('');
    const sujo = useAlteracaoPendente(nome, open && (tipo ?? 'novo'));
    const [erro, setErro] = useState<string | null>(null);
    const [salvando, setSalvando] = useState(false);

    useEffect(() => {
        if (!open) return;
        setNome(tipo?.nome ?? '');
        setErro(null);
    }, [open, tipo]);

    const salvar = async () => {
        setSalvando(true);
        setErro(null);
        try {
            const salvo = await dados().salvarTipoDeServico({ ...(tipo ? { id: tipo.id } : {}), nome });
            aviso({ title: tipo ? 'Tipo de serviço renomeado' : 'Tipo de serviço criado', description: salvo.nome });
            onSalvo();
            onClose();
        } catch (e) {
            setErro(e instanceof Error ? e.message : 'Não foi possível salvar o tipo de serviço.');
        } finally {
            setSalvando(false);
        }
    };

    return (
        <Modal
            open={open} onClose={onClose} width={440}
            title={tipo ? `Renomear ${tipo.nome}` : 'Novo tipo de serviço'}
            dirty={sujo} onSave={() => void salvar()}
            footer={(
                <>
                    <Button variant="secondary" onClick={onClose}>Cancelar</Button>
                    <Button onClick={() => void salvar()} loading={salvando} disabled={!nome.trim()}>{tipo ? 'Salvar' : 'Criar tipo'}</Button>
                </>
            )}
        >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                {erro && <Alert tone="danger" dismissible={false}>{erro}</Alert>}
                {tipo && tipo.prestadores > 0 && (
                    <Alert tone="info" dismissible={false}>
                        {tipo.prestadores === 1 ? 'Um prestador usa' : `${tipo.prestadores} prestadores usam`} este tipo. O nome novo vale para eles também.
                    </Alert>
                )}
                <FormField label="Nome" required htmlFor={idNome}>
                    <Input id={idNome} value={nome} onChange={setNome} placeholder="Ex.: Ar-condicionado" />
                </FormField>
            </div>
        </Modal>
    );
}
